import { Supplier } from '@/models/Supplier';
import { Model } from '@/models/Model';
import { Brand } from '@/models/Brand';
import { Category } from '@/models/Category';
import { ProductVariant } from './ProductVariant';

export class Product extends Model {
  protected $name: string = 'Product';
  protected $endpoint: string = '/products';

  protected $fillable: string[] = [
    'id',
    'name',
    'brand',
    'description',
    'type',
    'quantity',
    'search_cache',
    'status',
    'supplier',
    'supplier_style_name',
    'supplier_style_code',
    'shopify_product_id',
    'vendor',
  ];

  public id!: string;
  public name!: string;
  public description!: string;
  public type!: string;
  public quantity!: string;
  public search_cache!: string;
  public status!: string;
  public supplier_style_name!: string;
  public supplier_style_code!: string;
  public vendor!: string;
  public image_url!: string;
  public created_at!: string;
  public updated_at!: string;
  public shopify_product_id!: string;
  public product_id!: string;

  public brand?: Brand;
  public supplier?: Supplier;
  public categories?: Category[];
  public variants?: ProductVariant[];
  public images?: ProductImage[];
  public metadata?: Metadata;

  public quickAdd(): this {
    this.$endpoint = `/products/quick-add`;
    return this;
  }

  public attachImage(productId: string): this {
    this.$endpoint = `/products/${productId}/attach-image`;
    return this;
  }

  public setDefaultImage(productId: string, payload: any): Promise<any> {
    const url = `/products/${productId}/set-default-image`;
    return this.request('post', url, payload);
  }

  public detachImage(productId: string, payload: any): Promise<any> {
    const url = `/products/${productId}/detach-image`;
    return this.request('delete', url, payload);
  }

  public attachCategory(productId: string, categoryId: string): Promise<any> {
    const url = `/products/${productId}/relationships/category/${categoryId}`;
    return this.request('post', url, {});
  }

  public removeAttachedCategory(productId: string, categoryId: string): Promise<any> {
    const url = `/products/${productId}/relationships/category/${categoryId}`;
    return this.request('delete', url, {});
  }

  public deleteProduct(productId: string): Promise<any> {
    const url = `/products/${productId}`;
    return this.request('delete', url, {});
  }

  public importProducts(): this {
    this.$endpoint = `/products/imports`;
    return this;
  }

  // URLS for download/export
  public exportProducts(): string {
    return `v1/products/export`;
  }

  public exportProduct(productId: string): string {
    return `v1/products/${productId}/export`;
  }

  protected static instance?: Product;
  public static getInstance(instance: Product) {
    if (! this.instance) {
      this.instance = instance;
    }

    return this.instance;
  }
}

export interface ProductImage {
  id?: string;
  name?: string;
  url_large?: string;
  url_medium?: string;
  url_thumbnail?: string;
}

export interface Metadata {
  stock_allocated: number;
  stock_available: number;
  stock_on_hand: number;
  total_variants: number;
  has_missing_attributes: number;
  stock_purchase_order: number;
}

export enum ProductIncludes {
  BRAND = 'brand',
  IMAGES = 'images',
  SUPPLIER = 'supplier',
  CATEGORIES = 'categories',
  METADATA = 'metadata',
}
